/**
 * Created by katarinababic on 13.1.25.
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import { Container, Row, Col, Alert } from 'reactstrap';
import { Theme } from '../../../Theme/Theme';
import styled from '@emotion/styled';
import { useRootStore } from '../../../Store/useRootStore';
import { useLocalized } from '../../../Hooks/useLocalized';
import { MarkdownRenderer } from '../../Markdown/MarkdownRenderer';
import { WorkoutTemplateEntry } from '../../../Model/Explore/WorkoutTemplateEntry';
import { ViewPhase } from '../../../Model/WorkoutLog/ViewPhase';
import { ViewBlock } from '../../../Model/WorkoutLog/ViewBlock';
import { useEffect, useState } from 'react';
import { reaction } from 'mobx';
import { EMPTY_ARRAY } from '../../../Utils/Constants';
import { EventEntry } from '../../../Model/Coach/Schedule/EventEntry';
import { WorkoutTemplatePhase } from './WorkoutTemplatePhase';
import { useTrans } from '../../../Store/System/LocalizationStore';
import { ImmersiveWorkout } from './ImmersiveWorkout';
import { EndWorkoutOverviewModal } from './EndWorkoutOverviewModal';
import { ScheduleButton } from '../../ScheduleButton';
import { ShareButton } from '../../ShareButton';
import { ScheduleModal } from '../../ScheduleModal/ScheduleModal';
import { useGeneratedWorkout } from './useGeneratedWorkout';
import { ExploreContentViewWorkoutTemplateSkeleton } from './ExploreContentViewWorkoutTemplateSkeleton';
import { useEventParent } from '../../../Hooks/useEventEntryMutations';
import { useContentMetaTags } from '../../../Hooks/useContentMetaTags';
import { getMediaLarge } from '../../../Model/Media/Media';
import { useLocalizedImage } from '../../../Hooks/useLocalizedMedia';
import { usePlausible } from '../../../Contexts/PlausibleContext';

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 24px auto;
  padding: 0 24px 48px;

  @media (max-width: 768px) {
    padding: 8px 16px 32px;
  }
`;

const HeroSection = styled.div<{ backgroundImageUrl?: string }>`
  position: relative;
  height: 400px;
  border-radius: 20px;
  overflow: hidden;
  margin-bottom: 32px;
  cursor: pointer;
  background: ${({ backgroundImageUrl }) =>
    backgroundImageUrl ? `url('${backgroundImageUrl}')` : Theme.Colors.lightestGrey};
  background-size: cover;
  background-position: center;

  @media (min-width: 768px) {
    height: 480px;
  }

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.2) 50%, transparent 100%);
  }
`;

const HeroContent = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 32px;
  color: #fff;
  z-index: 2;
`;

const HeroTitle = styled.h1`
  font-size: 32px;
  font-weight: 700;
  margin: 0 0 8px;
  color: #fff;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const HeroMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.85);
`;

const MetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;

  & > i {
    font-size: 16px;
  }
`;

const PlayButtonOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80px;
  height: 80px;
  background: ${Theme.Colors.primary};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.3);
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    transform: translate(-50%, -50%) scale(1.08);
    box-shadow: 0 6px 32px rgba(0, 0, 0, 0.4);
  }

  & > i {
    font-size: 36px;
    color: #fff;
    margin-left: 4px;
  }
`;

const ContentSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 320px;
  gap: 48px;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

const MainContent = styled.div``;

const Sidebar = styled.div`
  @media (max-width: 992px) {
    order: -1;
  }
`;

const DescriptionText = styled.div`
  font-size: 15px;
  line-height: 1.7;
  color: ${Theme.Colors.charcoal};
  margin-bottom: 24px;

  p {
    margin-bottom: 16px;
  }
`;

const StartButton = styled.button`
  width: 100%;
  padding: 16px 24px;
  background: ${Theme.Colors.primary};
  color: #fff;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s ease;
  margin-bottom: 16px;

  &:hover {
    background: ${Theme.Colors.primaryDark};
    transform: translateY(-1px);
  }

  & > i {
    font-size: 20px;
  }
`;

const InfoCard = styled.div`
  background: ${Theme.Colors.white};
  border: 1px solid ${Theme.Colors.lighterGrey};
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 16px;
`;

const InfoCardTitle = styled.h4`
  font-size: 13px;
  font-weight: 600;
  color: ${Theme.Colors.warmGrey};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 12px;
`;

const InfoCardValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: ${Theme.Colors.textColor};
`;

type SectionData = { phase: ViewPhase; data: ViewBlock[] };

export type ExploreContentViewWorkoutTemplateProps = {
  entry: WorkoutTemplateEntry;
  parentEvent?: EventEntry;
  selectedEvent?: EventEntry;
};

export const ExploreContentViewWorkoutTemplate: React.FC<ExploreContentViewWorkoutTemplateProps> = observer(
  ({ entry, selectedEvent, parentEvent }) => {
    const { workoutViewStore } = useRootStore();
    const { t } = useTrans();
    const title = useLocalized(entry.name);
    const description = useLocalized(entry.description);
    const { trackPlausible } = usePlausible();

    const [sections, setSections] = React.useState<SectionData[]>([]);
    const [showCountdown, setShowCountdown] = useState(false);
    const [showSchedule, setShowSchedule] = React.useState(false);

    const { data: fetchedEventEntry, refetch: refetchEvent } = useEventParent(entry.type, entry.id);
    const eventEntry = fetchedEventEntry ?? parentEvent ?? selectedEvent;

    const { data: generatedWorkout, isLoading, isError, refetch: refetchWorkout } = useGeneratedWorkout({ entry });

    const image = useLocalizedImage(entry.images);
    const imageUrl = image ? getMediaLarge(image) : undefined;

    useContentMetaTags({ title, description, imageUrl });

    React.useEffect(() => {
      trackPlausible('Page View', { pageTitle: title, contentType: entry.type, contentId: entry.id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [title]);

    useEffect(() => {
      if (generatedWorkout) {
        workoutViewStore.selectWorkoutFromExploreTabTemplate(generatedWorkout, selectedEvent);
      }
      return () => workoutViewStore.workoutRunner.resetWorkout();
    }, [generatedWorkout, selectedEvent, workoutViewStore]);

    useEffect(
      () =>
        reaction(
          () => workoutViewStore.viewWorkout?.viewPhases ?? EMPTY_ARRAY,
          (viewPhases) =>
            setSections(
              viewPhases.reduce((arr, phase) => {
                const prev = arr[arr.length - 1];
                if (prev?.phase.type === phase.type) {
                  prev.data = prev.data.concat(phase.exerciseBlocks);
                } else {
                  arr.push({ phase: phase, data: phase.exerciseBlocks.slice() });
                }
                return arr;
              }, [] as SectionData[]),
            ),
        ),
      [workoutViewStore],
    );

    // NOTE: Removed refetch on showCountdown - was causing race condition
    // The workout is already loaded when user clicks play

    const handleShowScheduleModal = React.useCallback(() => setShowSchedule(true), []);

    const handleCloseScheduleModal = React.useCallback(() => {
      setShowSchedule(false);
      refetchEvent();
    }, [refetchEvent]);

    if (workoutViewStore.isWorkoutActive || showCountdown) {
      return <ImmersiveWorkout onExit={() => setShowCountdown(false)} />;
    }

    if (isLoading) {
      return <ExploreContentViewWorkoutTemplateSkeleton />;
    }

    if (isError || !generatedWorkout) {
      return (
        <Container style={{ paddingTop: '2rem' }}>
          <Row>
            <Col>
              <Alert color="danger">
                <h4 className="alert-heading">
                  {t('workout.loadingError.title', { defaultValue: 'Could not load the training' })}
                </h4>
                <p>{t('workout.loadingError.message', { defaultValue: 'Try again later, please' })}</p>
              </Alert>
            </Col>
          </Row>
        </Container>
      );
    }

    const totalExercises = sections.reduce((sum, s) => sum + s.data.length, 0);
    const estimatedDuration = generatedWorkout?.estimatedTotalExerciseTime
      ? Math.round(generatedWorkout.estimatedTotalExerciseTime / 60000)
      : undefined;

    return (
      <PageContainer>
        <HeroSection backgroundImageUrl={imageUrl} onClick={() => setShowCountdown(true)}>
          <PlayButtonOverlay>
            <i className="mdi mdi-play" />
          </PlayButtonOverlay>
          <HeroContent>
            <HeroTitle>{title}</HeroTitle>
            <HeroMeta>
              {estimatedDuration && (
                <MetaItem>
                  <i className="mdi mdi-clock-outline" />
                  {estimatedDuration} {t('globals.min', { defaultValue: 'min' })}
                </MetaItem>
              )}
              <MetaItem>
                <i className="mdi mdi-dumbbell" />
                {totalExercises} {t('globals.exercises', { defaultValue: 'exercises' })}
              </MetaItem>
            </HeroMeta>
          </HeroContent>
        </HeroSection>

        <ContentSection>
          <MainContent>
            {description && (
              <DescriptionText>
                <MarkdownRenderer text={description} />
              </DescriptionText>
            )}
            {sections.map(({ phase, data }) => (
              <WorkoutTemplatePhase key={phase.viewPhaseId} phase={phase} data={data} />
            ))}
          </MainContent>

          <Sidebar>
            <StartButton onClick={() => setShowCountdown(true)}>
              <i className="mdi mdi-play" />
              {t('workout.startWorkout', { defaultValue: 'Start Workout' })}
            </StartButton>

            <ShareButton data={{ title, description, imageUrl, contentType: 'workoutTemplate', contentId: entry.id }} />
            <ScheduleButton event={eventEntry} onClick={handleShowScheduleModal} />

            {estimatedDuration && (
              <InfoCard>
                <InfoCardTitle>{t('workout.duration', { defaultValue: 'Duration' })}</InfoCardTitle>
                <InfoCardValue>{estimatedDuration} min</InfoCardValue>
              </InfoCard>
            )}

            <InfoCard>
              <InfoCardTitle>{t('workout.exercises', { defaultValue: 'Exercises' })}</InfoCardTitle>
              <InfoCardValue>{totalExercises}</InfoCardValue>
            </InfoCard>
          </Sidebar>
        </ContentSection>

        {showSchedule && (
          <ScheduleModal
            key={eventEntry?.id ?? entry.id}
            exploreEntry={entry}
            parentEvent={eventEntry}
            currentEvent={eventEntry}
            disableRecurring={false}
            onSchedule={handleCloseScheduleModal}
            onDelete={handleCloseScheduleModal}
          />
        )}
        {workoutViewStore.endWorkoutOverviewModalVisible && <EndWorkoutOverviewModal />}
      </PageContainer>
    );
  },
);
